import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * SectionCard — the canonical "document card" wrapper used on /account,
 * /equipment, /cylinders, and elsewhere. Navy header band with white
 * icon + title, white content area below.
 *
 *   <SectionCard icon={<Wrench size={18} />} title="Equipment" subtitle="…">
 *     …content…
 *   </SectionCard>
 *
 * The header is the consistent navy-on-white treatment Sky asked for —
 * one source of truth so every menu/page section matches.
 */
export function SectionCard({
  id,
  icon,
  title,
  subtitle,
  actions,
  danger,
  children,
  className,
}: {
  /** Optional anchor id so the section can be deep-linked (e.g. #contracts). */
  id?: string;
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-side controls inside the navy header band (e.g. a small button). */
  actions?: ReactNode;
  /** Red-700 header band instead of navy — used for sign-in trouble rail. */
  danger?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      id={id}
      className={cn(
        'rounded-2xl border-2 bg-brix-surface overflow-hidden shadow-card-deep',
        danger ? 'border-red-300' : 'border-brix-primary-deep',
        className,
      )}
    >
      <div
        className={cn(
          'px-6 md:px-8 py-4 flex items-start gap-3',
          danger ? 'bg-red-600' : 'bg-brix-primary-deep',
        )}
      >
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center text-white shrink-0">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-xl font-bold tracking-tight text-white leading-none">
            {title}
          </h2>
          {subtitle && (
            <p className="text-sm text-white/85 tracking-tight mt-1.5 leading-snug">
              {subtitle}
            </p>
          )}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>
      <div className="px-6 md:px-8 py-6">{children}</div>
    </section>
  );
}
